import React, { useEffect } from "react";
import { CategoryV2, Product } from "src/shared/services/interfacev2";
import { putProduct } from "../../../../../shared/services/services";
import { closeLoading, notifyError, notifyLoading, notifySuccess } from "../../../../../assets/toastService";
import { errorHandling } from "src/shared/services/errorHandling";
import { productToFormState, useProductForm } from "./shared/useProductForm";
import ProductFormModal from "./shared/ProductFormModal";

type Props = {
  editData: Product;
  close: (data: any) => void;
  category: CategoryV2[];
  loading: (state: boolean) => void;
};

export default function EditProducts({ editData, close, category, loading }: Props) {
  const {
    form, setForm, setField, uom,
    productType, switchProductType,
    barcodeMode, scanMode, setScanMode,
    submit, setSubmit,
    handleImageChange, handleBarcodeDetected,
    discountPrice, finalPrice, discountString, sellingPrice, purchasePrice,
  } = useProductForm({ category, initialState: productToFormState(editData) });

  // Sync UOM from server if returned as strings
  useEffect(() => {
    if (uom.length === 0) return;
    if (editData.unitConfiguration.uom[0] && typeof editData.unitConfiguration.uom[0] === "string") {
      const synced = (editData.unitConfiguration.uom as unknown as string[])
        .map((name) => uom.find((u) => u.uomName === name))
        .filter(Boolean) as unknown as { id: string; uomName: string }[];
      setForm((prev) => ({
        ...prev,
        uom: synced,
        amountMeasurement: editData.unitConfiguration.baseUnitType,
      }));
    }
  }, [uom]);

  const checkMandatory = () => {
    if (!form.productName || !form.categoryId) return false;
    if (productType === "Inward" && form.uom.length > 0) return !!form.amountMeasurement;
    if (productType === "Service") return !!form.amount;
    return true;
  };

  const hasChanges = () => {
    const e = editData;
    if (form.productName !== e.productName) return true;
    if (form.arabicName !== e.arabicName) return true;
    if (form.amount !== e.amount) return true;
    if (form.type !== (e.type ?? "")) return true;
    if (form.categoryId !== e.categoryId) return true;
    if (form.barcodeId !== e.barcodeId) return true;
    if (form.amountMeasurement !== e.unitConfiguration.baseUnitType) return true;
    if (String(form.sellingPrice) !== e.sellingPrice) return true;
    if (String(form.purchasePrice) !== e.purchasePrice) return true;
    if (discountString !== String(e.discount)) return true;
    if (JSON.stringify(form.uom) !== JSON.stringify(e.unitConfiguration.uom)) return true;
    if (form.image instanceof File) return true;
    return false;
  };

  const canSubmit = checkMandatory() && hasChanges() && !submit;

  const handleSubmit = async () => {
    setSubmit(true);
    notifyLoading("Updating product...");
    loading(true);
    try {
      const payload = new FormData();
      const toN = (v: any) => Number(v) || 0;

      const appendIfChanged = (key: string, next: any, prev: any) => {
        if (next !== prev) payload.append(key, String(next));
      };
      if (form.productName !== editData.productName) payload.append("productName", form.productName);
      if (form.arabicName !== editData.arabicName) payload.append("arabicName", form.arabicName);
      if (form.categoryId !== editData.categoryId) payload.append("categoryId", form.categoryId);
      if (form.barcodeId !== editData.barcodeId) payload.append("barcodeId", form.barcodeId);
      if (form.amountMeasurement !== editData.unitConfiguration.baseUnitType) payload.append("baseUnitType", form.amountMeasurement);
      if (JSON.stringify(form.uom) !== JSON.stringify(editData.unitConfiguration.uom))
        payload.append("uom", JSON.stringify(form.uom.map((u) => u.id)));

      appendIfChanged("sellingPrice", toN(form.sellingPrice), toN(editData.sellingPrice));
      appendIfChanged("purchasePrice", toN(form.purchasePrice), toN(editData.purchasePrice));
      appendIfChanged("discount", discountString, editData.discount);

      if (form.image instanceof File) payload.append("image", form.image);

      if (payload.entries().next().done) {
        notifyError("No changes detected");
        return;
      }

      await putProduct(editData._id.$oid, payload);
      notifySuccess("Product updated successfully");
      close("");
    } catch (err) {
      errorHandling(err);
    } finally {
      closeLoading();
      setSubmit(false);
      loading(false);
    }
  };

  return (
    <ProductFormModal
      isOpen={!!editData}
      title="Edit Product"
      productType={productType}
      switchProductType={switchProductType}
      form={form}
      setField={setField}
      setForm={setForm}
      category={category}
      uom={uom}
      barcodeMode={barcodeMode}
      scanMode={scanMode}
      onToggleScan={() => setScanMode((p) => !p)}
      onBarcodeDetected={handleBarcodeDetected}
      onImageChange={handleImageChange}
      discountPrice={discountPrice}
      finalPrice={finalPrice}
      sellingPrice={sellingPrice}
      purchasePrice={purchasePrice}
      canSubmit={canSubmit}
      submit={submit}
      onSubmit={handleSubmit}
      onClose={() => close("")}
      serverImageUrl={editData.imageUrl}
      wholeSalePrice={Number(form.wholeSalePrice) || 0}
    />
  );
}